import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowLeft, Plus } from "@phosphor-icons/react";

const CHECKLIST = [
  "Power on", "Screen intact", "Buttons working", "Camera working",
  "Charging port OK", "Speaker OK", "Back cover intact", "Sim tray present",
];

export default function NewRepairTicket() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "" });

  const [form, setForm] = useState({
    device_make: "", device_model: "", imei: "", passcode: "",
    issue: "", estimated_cost: 0,
  });
  const [checklist, setChecklist] = useState({});

  const loadCustomers = (q = "") => api.get("/customers", { params: q ? { q } : {} }).then((r) => setCustomers(r.data));
  useEffect(() => { loadCustomers(); }, []);
  useEffect(() => { const t = setTimeout(() => loadCustomers(search), 200); return () => clearTimeout(t); }, [search]);

  const addCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post("/customers", newCust);
      setCustomers((c) => [data, ...c]);
      setCustomerId(data.id);
      setShowNewCustomer(false);
      setNewCust({ name: "", phone: "", email: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customerId) return toast.error("Select a customer");
    try {
      const { data } = await api.post("/repair-tickets", {
        customer_id: customerId,
        device_make: form.device_make,
        device_model: form.device_model,
        imei: form.imei,
        passcode: form.passcode,
        issue: form.issue,
        estimated_cost: Number(form.estimated_cost || 0),
        intake_checklist: checklist,
      });
      toast.success(`Ticket ${data.ticket_number} created`);
      nav(`/repairs/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Layout>
      <PageHeader
        overline="INTAKE"
        title="New repair ticket"
        actions={
          <button onClick={() => nav(-1)} className="h-11 px-4 border border-[#E5E7EB] text-[13px] font-semibold flex items-center gap-2 hover:border-[#121216]">
            <ArrowLeft size={14} weight="bold" /> BACK
          </button>
        }
      />
      <form onSubmit={submit} className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="new-ticket-form">
        {/* Customer */}
        <section className="lg:col-span-1 bg-white border border-[#E5E7EB] p-5 h-fit">
          <div className="overline mb-3">CUSTOMER</div>
          {!showNewCustomer ? (
            <>
              <input
                placeholder="Search customers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none mb-2"
                data-testid="cust-search"
              />
              <div className="max-h-64 overflow-auto border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
                {customers.length === 0 ? (
                  <div className="p-3 text-sm text-zinc-500">No customers.</div>
                ) : customers.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    data-testid={`cust-opt-${c.id}`}
                    onClick={() => setCustomerId(c.id)}
                    className={`w-full text-left p-3 hover:bg-[#F8FAFC] ${customerId === c.id ? "bg-[#1E5FEB]/5 border-l-2 border-[#1E5FEB]" : ""}`}
                  >
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs font-mono text-zinc-500">{c.phone || "—"}</div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowNewCustomer(true)}
                className="mt-3 w-full h-10 border border-[#121216] text-xs font-semibold hover:bg-[#121216] hover:text-white flex items-center justify-center gap-2"
                data-testid="new-cust-open"
              >
                <Plus size={12} weight="bold" /> ADD NEW CUSTOMER
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <Field label="Name"><input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className={fld} data-testid="new-cust-name" /></Field>
              <Field label="Phone"><input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} className={fld} /></Field>
              <Field label="Email"><input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} className={fld} /></Field>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={addCustomer} data-testid="new-cust-save" className="flex-1 h-10 bg-[#121216] text-white text-xs font-semibold">SAVE</button>
                <button type="button" onClick={() => setShowNewCustomer(false)} className="h-10 px-4 border border-[#E5E7EB] text-xs">CANCEL</button>
              </div>
            </div>
          )}
        </section>

        {/* Device + Issue */}
        <section className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E5E7EB] p-5">
            <div className="overline mb-4">DEVICE</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Make (e.g. Apple)"><input required value={form.device_make} onChange={(e) => setForm({ ...form, device_make: e.target.value })} className={fld} data-testid="device-make" /></Field>
              <Field label="Model (e.g. iPhone 14)"><input required value={form.device_model} onChange={(e) => setForm({ ...form, device_model: e.target.value })} className={fld} data-testid="device-model" /></Field>
              <Field label="IMEI / Serial"><input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} className={fld + " font-mono"} data-testid="device-imei" /></Field>
              <Field label="Passcode / Pattern"><input value={form.passcode} onChange={(e) => setForm({ ...form, passcode: e.target.value })} className={fld + " font-mono"} /></Field>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] p-5">
            <div className="overline mb-4">ISSUE & ESTIMATE</div>
            <Field label="Issue / Reported problem">
              <textarea required value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} rows={3} className={`${fld} h-auto py-2`} data-testid="device-issue" />
            </Field>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Estimated cost ($)">
                <input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className={fld + " font-mono"} data-testid="estimated-cost" />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] p-5">
            <div className="overline mb-4">INTAKE CHECKLIST</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CHECKLIST.map((item) => (
                <label key={item} className="flex items-center gap-2 text-xs border border-[#E5E7EB] px-2 py-2 cursor-pointer hover:bg-[#F8FAFC]">
                  <input
                    type="checkbox"
                    checked={!!checklist[item]}
                    onChange={(e) => setChecklist({ ...checklist, [item]: e.target.checked })}
                    className="accent-[#1E5FEB]"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            data-testid="submit-ticket"
            className="h-14 px-8 bg-[#1E5FEB] hover:bg-[#1849C8] text-white font-heading font-bold tracking-wide flex items-center gap-2 transition-colors"
          >
            <FloppyDisk size={16} weight="bold" /> CREATE TICKET
          </button>
        </section>
      </form>
    </Layout>
  );
}

const fld = "w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}
